import React, { type FC, type ReactNode, Suspense, use, useCallback, useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { Link } from "react-router-dom";
import { cn } from "@heroui/react";
import { animate, useReducedMotion } from "framer-motion";
import { GQL, Utils } from "@binale-tech/shared";
import dayjs from "dayjs";

import { AppRoutes } from "@domain/routing/routeConstants";
import { CompanyAccountingYearContext, CompanyContext } from "@domain/context/CompanyContext";
import { ReadinessContext } from "@domain/context/DataReadyness";
import { UserContext } from "@domain/context/UserProvider";
import { ProductAccessUtils } from "@domain/models/utils/ProductAccessUtils";
import { ProductDetails, ProductKeys } from "@domain/models/Product";
import { useGuVMetrics } from "@accounting/common/metrics/useGuVMetrics";
import { useOpenPositions } from "@accounting/common/metrics/useOpenPositions";
import { useOpenPositionEvents } from "@accounting/common/calendar/useOpenPositionEvents";
import { useVatPosition } from "@accounting/common/metrics/useVatPosition";
import { useBankMetrics } from "@accounting/common/metrics/useBankMetrics";
import { useCashMetrics } from "@accounting/common/metrics/useCashMetrics";
import { GlobalYearSelect } from "@accounting/shared/GenericTableShell/Toolbar/components/YearSelect";
import { HomeWidgetShell } from "@app/pages/home/HomeWidgetShell";
import { HomeExpandToggle } from "@app/pages/home/HomeExpandToggle";
import "./HomeMetricsWidget.css";

const GuVBarsChart = React.lazy(() => import("./charts/GuVBarsChart").then((m) => ({ default: m.GuVBarsChart })));
const BalanceAreaChart = React.lazy(() =>
    import("./charts/BalanceAreaChart").then((m) => ({ default: m.BalanceAreaChart })),
);

/**
 * Currency amount that renders its value immediately and counts up on later
 * value changes (skipped under prefers-reduced-motion).
 */
const AnimatedAmount: FC<{ cents: number; className?: string }> = ({ cents, className }) => {
    const ref = useRef<HTMLSpanElement>(null);
    const prev = useRef(cents);
    const reduced = useReducedMotion();
    useEffect(() => {
        const el = ref.current;
        if (!el || prev.current === cents) {
            return undefined;
        }
        if (reduced) {
            el.textContent = Utils.CurrencyUtils.currencyFormat(cents);
            prev.current = cents;
            return undefined;
        }
        const controls = animate(prev.current, cents, {
            duration: 0.8,
            ease: "easeOut",
            onUpdate: (v) => {
                el.textContent = Utils.CurrencyUtils.currencyFormat(Math.round(v));
            },
        });
        prev.current = cents;
        return () => controls.stop();
    }, [cents, reduced]);
    return (
        <span ref={ref} className={cn("tabular-nums", className)}>
            {Utils.CurrencyUtils.currencyFormat(cents)}
        </span>
    );
};

const Row: FC<{ label: ReactNode; cents: number; to: string; danger?: boolean }> = ({ label, cents, to, danger }) => (
    <Link
        to={to}
        className={cn(
            "flex items-baseline justify-between gap-6 hover:opacity-80",
            danger ? "text-red-500" : "text-app-text",
        )}
    >
        <span className="text-sm opacity-70">{label}</span>
        <AnimatedAmount cents={cents} className="font-mono text-base font-semibold" />
    </Link>
);

const Skeleton: FC = () => (
    <div
        data-testid="home-metrics-skeleton"
        className="HomeMetricsWidget w-120 rounded-2xl bg-app-surface/50 backdrop-blur-md border border-app-surface/80 shadow-lg p-4"
    >
        <div className="animate-pulse space-y-3">
            <div className="h-3 w-24 rounded bg-app-text/20" />
            <div className="h-3 w-full rounded bg-app-text/10" />
            <div className="h-3 w-full rounded bg-app-text/10" />
            <div className="h-3 w-2/3 rounded bg-app-text/10" />
        </div>
    </div>
);

// Exact chart heights - a mismatched fallback makes the card height jump when the lazy chunk resolves
const guvChartFallback = <div className="h-[170px] animate-pulse rounded bg-app-text/10" />;
const balanceChartFallback = <div className="h-[140px] animate-pulse rounded bg-app-text/10" />;

export const HomeMetricsWidget: FC = () => {
    const { companyGQL } = use(CompanyContext);
    const user = use(UserContext);
    const { yearConfig } = use(CompanyAccountingYearContext);
    const { isReady } = use(ReadinessContext);

    const guv = useGuVMetrics();
    const op = useOpenPositions();
    const vat = useVatPosition();
    const bank = useBankMetrics();
    const cash = useCashMetrics();
    const opEvents = useOpenPositionEvents();

    const [expanded, setExpanded] = useState(false);
    const toggle = useCallback(() => setExpanded((v) => !v), []);

    if (!ProductAccessUtils.canRead(ProductKeys.AccountingCommon, companyGQL, user)) {
        return null;
    }
    if (!isReady) {
        return <Skeleton />;
    }

    const canBanks = ProductAccessUtils.canRead(ProductKeys.Banks, companyGQL, user);
    const canCash = ProductAccessUtils.canRead(ProductKeys.CashRegister, companyGQL, user);
    const isKust = yearConfig?.taxation === GQL.ICompanyTaxation.Kust;
    const showGuV = guv.status === "ready";
    const showOp = op.status === "ready";
    const showVat = !isKust && vat.status === "ready";
    const showBank = canBanks && bank.status === "ready";
    const showCash = canCash && cash.status === "ready";
    if (!showGuV && !showOp && !showVat && !showBank && !showCash) {
        return null;
    }

    const banksLink = ProductDetails.details[ProductKeys.Banks].link;
    const cashLink = ProductDetails.details[ProductKeys.CashRegister].link;
    const vatMonth = dayjs()
        .month(vat.period - 1)
        .format("MMMM");

    // Overdue receivables (debitor side of the "überfällig" aging bucket).
    const overdue =
        opEvents.status === "ready" ? opEvents.buckets.find((b) => b.key === "ueberfaellig")?.debitor : undefined;
    // Liquidity = bank + cash on hand (whichever the user can see).
    const liquidity = (showBank ? bank.eurBalance : 0) + (showCash ? cash.total : 0);

    const header = (
        <div className="flex w-full items-center justify-between gap-4">
            <span className="flex items-center gap-3">
                <GlobalYearSelect />
                <span className="text-sm font-bold uppercase tracking-wide opacity-70">
                    <FormattedMessage id="app.home.metrics.title" />
                </span>
            </span>
            <HomeExpandToggle expanded={expanded} onToggle={toggle} />
        </div>
    );

    const collapsedBody = (
        <div className="mt-3 space-y-2">
            {showGuV && (
                <Row
                    label={<FormattedMessage id="app.home.metrics.betriebsergebnis" />}
                    cents={guv.operatingResult}
                    to={AppRoutes.BWA}
                />
            )}
            {showGuV && (
                <Row label={<FormattedMessage id="app.home.metrics.profit" />} cents={guv.profit} to={AppRoutes.BWA} />
            )}
            {showOp && (
                <Row
                    label={<FormattedMessage id="app.home.metrics.op" />}
                    cents={op.receivables.total - op.payables.total}
                    to={AppRoutes.accountsView}
                />
            )}
            {overdue && overdue.count > 0 && (
                <Row
                    label={
                        <>
                            <FormattedMessage id="app.home.metrics.overdueReceivables" /> ({overdue.count})
                        </>
                    }
                    cents={overdue.sumCents}
                    to={AppRoutes.debitorAnalysisOpos}
                    danger
                />
            )}
            {showVat && (
                <Row
                    label={<FormattedMessage id="app.home.metrics.vat" values={{ month: vatMonth }} />}
                    cents={vat.amount}
                    to={AppRoutes.UStVA}
                />
            )}
            {(showBank || showCash) && (
                <Row
                    label={<FormattedMessage id="app.home.metrics.liquidity" />}
                    cents={liquidity}
                    to={showBank ? banksLink : cashLink}
                />
            )}
        </div>
    );

    const expandedBody = (
        <div
            className={cn(
                "mt-4 grid gap-x-8",
                (showBank || showCash) && (showGuV || showOp || showVat) ? "grid-cols-2" : "grid-cols-1",
            )}
        >
            {(showBank || showCash) && (
                <div className="space-y-5">
                    {showBank && (
                        <section>
                            <span className="text-xs font-bold uppercase opacity-60">
                                <FormattedMessage id="app.home.metrics.bank" /> ·{" "}
                                <FormattedMessage id="app.home.metrics.bank.trend" />
                            </span>
                            <div className="mt-1">
                                <Row label="EUR" cents={bank.eurBalance} to={banksLink} />
                            </div>
                            <Suspense fallback={balanceChartFallback}>
                                <BalanceAreaChart series={bank.series} />
                            </Suspense>
                        </section>
                    )}
                    {showCash && (
                        <section>
                            <Link to={cashLink} className="text-xs font-bold uppercase opacity-60 hover:opacity-90">
                                <FormattedMessage id="app.home.metrics.cash" />
                            </Link>
                            <div className="mt-1 space-y-1">
                                {cash.registers.map((register) => (
                                    <Row
                                        key={register.id}
                                        label={register.name}
                                        cents={register.balance}
                                        to={cashLink}
                                    />
                                ))}
                            </div>
                        </section>
                    )}
                </div>
            )}
            {(showGuV || showOp || showVat) && (
                <div className="space-y-5">
                    {showGuV && (
                        <section>
                            <Link
                                to={AppRoutes.BWA}
                                className="text-xs font-bold uppercase opacity-60 hover:opacity-90"
                            >
                                <FormattedMessage id="app.home.metrics.guv" />
                            </Link>
                            <div className="mt-1 space-y-1">
                                <Row
                                    label={<FormattedMessage id="app.home.metrics.revenue" />}
                                    cents={guv.revenue}
                                    to={AppRoutes.BWA}
                                />
                                <Row
                                    label={<FormattedMessage id="app.home.metrics.costs" />}
                                    cents={guv.costs}
                                    to={AppRoutes.BWA}
                                />
                                <Row
                                    label={<FormattedMessage id="app.home.metrics.profit" />}
                                    cents={guv.profit}
                                    to={AppRoutes.BWA}
                                />
                            </div>
                            <Suspense fallback={guvChartFallback}>
                                <GuVBarsChart monthlyRevenue={guv.monthlyRevenue} monthlyCosts={guv.monthlyCosts} />
                            </Suspense>
                        </section>
                    )}
                    {showOp && (
                        <section>
                            <span className="text-xs font-bold uppercase opacity-60">
                                <FormattedMessage id="app.home.metrics.op" />
                            </span>
                            <div className="mt-1 space-y-1">
                                <Row
                                    label={
                                        <>
                                            <FormattedMessage id="app.home.metrics.op.receivables" />{" "}
                                            <FormattedMessage
                                                id="app.home.metrics.op.count"
                                                values={{ count: op.receivables.count }}
                                            />
                                        </>
                                    }
                                    cents={op.receivables.total}
                                    to={AppRoutes.accountsView}
                                />
                                <Row
                                    label={
                                        <>
                                            <FormattedMessage id="app.home.metrics.op.payables" />{" "}
                                            <FormattedMessage
                                                id="app.home.metrics.op.count"
                                                values={{ count: op.payables.count }}
                                            />
                                        </>
                                    }
                                    cents={op.payables.total}
                                    to={AppRoutes.accountsView}
                                />
                            </div>
                        </section>
                    )}
                    {showVat && (
                        <section>
                            <Link
                                to={AppRoutes.UStVA}
                                className="text-xs font-bold uppercase opacity-60 hover:opacity-90"
                            >
                                <FormattedMessage id="app.home.metrics.vat" values={{ month: vatMonth }} />
                            </Link>
                            <div className="mt-1">
                                <Row
                                    label={
                                        <FormattedMessage
                                            id={
                                                vat.amount >= 0
                                                    ? "app.home.metrics.vat.zahllast"
                                                    : "app.home.metrics.vat.erstattung"
                                            }
                                        />
                                    }
                                    cents={vat.amount}
                                    to={AppRoutes.UStVA}
                                />
                            </div>
                        </section>
                    )}
                </div>
            )}
        </div>
    );

    return (
        <HomeWidgetShell
            className="HomeMetricsWidget"
            expanded={expanded}
            expandedWidth="w-[1120px]"
            header={header}
            collapsed={collapsedBody}
        >
            {expandedBody}
        </HomeWidgetShell>
    );
};
